'use client';

import { useEffect, useState, useRef } from 'react';

const getClientBuildTime = () => {
  if (typeof window === 'undefined') return null;
  return process.env.NEXT_PUBLIC_BUILD_TIME || window.__NEXT_BUILD_TIME || null;
};

export default function AppUpdateChecker() {
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [autoRefreshCountdown, setAutoRefreshCountdown] = useState(null);
  const lastServerBuildTime = useRef(null);
  const refreshingRef = useRef(false);

  useEffect(() => {
    let countdownInterval = null;

    const triggerRefresh = (serverBuildTime) => {
      if (refreshingRef.current) return;
      refreshingRef.current = true;

      setShowUpdateBanner(true);
      setAutoRefreshCountdown(5);

      let countdown = 5;
      countdownInterval = setInterval(() => {
        countdown--;
        setAutoRefreshCountdown(countdown);

        if (countdown <= 0) {
          clearInterval(countdownInterval);
          if (serverBuildTime) {
            localStorage.setItem('app_build_time', String(serverBuildTime));
          }
          window.location.reload();
        }
      }, 1000);
    };

    const checkForUpdates = async () => {
      let serverBuildTime = null;
      try {
        const res = await fetch('/api/build-version', { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          serverBuildTime = data.buildTime ? String(data.buildTime) : null;
          lastServerBuildTime.current = serverBuildTime;
        }
      } catch {
        return;
      }

      if (!serverBuildTime) return;

      const clientBuildTime = getClientBuildTime();
      const storedBuildTime = localStorage.getItem('app_build_time');

      // Stale JS bundle after deploy (main cause of Failed to find Server Action)
      if (clientBuildTime && clientBuildTime !== serverBuildTime) {
        triggerRefresh(serverBuildTime);
        return;
      }

      if (!storedBuildTime) {
        localStorage.setItem('app_build_time', serverBuildTime);
        return;
      }

      if (storedBuildTime !== serverBuildTime) {
        triggerRefresh(serverBuildTime);
      }
    };

    checkForUpdates();
    const interval = setInterval(checkForUpdates, 45000);

    return () => {
      clearInterval(interval);
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, []);

  const handleRefreshNow = () => {
    const buildTime = lastServerBuildTime.current || getClientBuildTime();
    if (buildTime) {
      localStorage.setItem('app_build_time', String(buildTime));
    }
    window.location.reload();
  };

  if (!showUpdateBanner) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-blue-600 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="font-semibold">New version deployed</p>
            <p className="text-sm text-blue-100">
              {autoRefreshCountdown !== null
                ? `Refreshing in ${autoRefreshCountdown}s…`
                : 'Please refresh to continue'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleRefreshNow}
          className="bg-white text-blue-600 px-4 py-2 rounded-md font-semibold hover:bg-blue-50 transition-colors"
        >
          Refresh now
        </button>
      </div>
    </div>
  );
};
